import { setExtra as sentrySetExtra } from '@sentry/browser';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import { getFileExtension, getPhotoUrl } from 'engine/api';
import { resize } from 'engine/resizeImage';
import { useStore } from 'engine/store';
import { X, Camera, LoaderCircle, Images, ShieldAlert } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import type { TaskAnswerPhoto } from 'shared/modelTypes/client';

export const PhotoQuestionForm = ({
	answer,
	readOnly,
	onSubmit,
}: {
	answer?: TaskAnswerPhoto;
	readOnly: boolean;
	onSubmit: (answer: File, text?: string) => void;
}) => {
	const eventId = useStore((state) => state.eventId);
	const teamId = useStore((s) => s.teamId);
	const isFrozen = useStore((s) => s.isFrozen);

	const fileExt = answer ? getFileExtension(answer.filename) : undefined;

	const [previewUrl, setPreviewUrl] = useState<string | null>(
		answer && fileExt ? getPhotoUrl(answer.id, fileExt, teamId!, eventId!) : null,
	);
	const [file, setFile] = useState<File | null>(null);
	const messageRef = useRef<HTMLInputElement>(null);

	const fileInputRef = useRef<HTMLInputElement>(null);

	const [isUploading, setIsUploading] = useState(false);

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		setIsUploading(true);
		if (file) {
			console.log(file.size);
			setPreviewUrl(URL.createObjectURL(file));

			const resizedImage = await resize(file, {
				width: 2048,
				height: 2048,
			});
			setFile(resizedImage);
			setPreviewUrl(URL.createObjectURL(resizedImage));
			setIsUploading(false);
		}
	};

	const handleRemoveImage = () => {
		setPreviewUrl(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleSubmit = () => {
		if (file) {
			sentrySetExtra('lastUpload', { ...file });
			onSubmit(file, messageRef?.current?.value || undefined);
		}
	};

	const state = useStore.getState();
	const isCaptain: boolean = state.teams.find((t) => t.id === state.teamId)?.captainId == state.deviceId;

	const [showTextField, setShowTextField] = useState((!readOnly && file) || (readOnly && answer?.message));

	useEffect(() => {
		setShowTextField((!readOnly && file) || (readOnly && answer?.message));
	}, [answer?.message, file, readOnly]);
	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				handleSubmit();
			}}
			className="flex flex-col gap-4"
		>
			<div className="relative">
				<input
					ref={fileInputRef}
					type="file"
					accept="image/*"
					onChange={handleFileChange}
					className="hidden"
					disabled={readOnly || isUploading}
				/>

				<div
					className={`relative aspect-square w-full overflow-hidden rounded-lg border-2 border-dashed skin-border-input ${
						!readOnly ? 'cursor-pointer hover:opacity-95' : ''
					}`}
				>
					{previewUrl ? (
						<>
							<img src={previewUrl} alt="Preview" className="size-full object-cover" />
							{!readOnly && (
								<button
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										handleRemoveImage();
									}}
									className="absolute right-2 top-2 rounded-full border border-gray-200 bg-white/80 p-1 shadow-sm shadow-black/30 hover:bg-white"
								>
									<X className="size-5 text-gray-950" />
								</button>
							)}
						</>
					) : (
						<div className="flex size-full columns-2 flex-row items-center justify-evenly gap-2 gap-x-8">
							<div
								className="flex flex-col items-center"
								onClick={(e) => {
									e.preventDefault();
									fileInputRef.current?.setAttribute('capture', 'environment');
									if (!readOnly) fileInputRef.current?.click();
								}}
							>
								<Camera className="size-12 opacity-90 skin-text" />
								<span className="text-sm opacity-80 skin-text">{__`Take a photo`}</span>
							</div>
							<div
								className="flex flex-col items-center"
								onClick={(e) => {
									e.preventDefault();
									fileInputRef.current?.removeAttribute('capture');
									if (!readOnly) fileInputRef.current?.click();
								}}
							>
								<Images className="size-12 opacity-90 skin-text" />
								<span className="text-sm opacity-80 skin-text">{__`Upload an image`}</span>
							</div>
							{readOnly && (
								<div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/40">
									<div className="flex w-full flex-col items-center bg-red-500/80 py-2 text-white shadow-lg">
										<ShieldAlert className="mb-1 size-6" />
										<p className="text-sm font-medium">
											{isFrozen
												? __`Game has been frozen by the game master. Uploading is currently not available.`
												: isCaptain
													? __`Upload not available.`
													: __`Upload not available in spectator mode.`}
										</p>
									</div>
								</div>
							)}
						</div>
					)}
				</div>
				{showTextField && (
					<Input
						// Move down
						ref={messageRef}
						value={answer?.message}
						readOnly={readOnly}
						placeholder={__`Type a message... (optional)`}
						id="text"
						className="mt-1 w-full"
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault();
							}
						}}
					/>
				)}
			</div>

			<div>
				{!readOnly && previewUrl && !isUploading ? (
					<Button className="w-full" type="submit">
						{__`Submit`}
					</Button>
				) : isUploading ? (
					<LoaderCircle className="mx-auto animate-spin" />
				) : (
					<></>
				)}
			</div>
		</form>
	);
};
